<div class="bn-app-shield-waf-page">
  <form [formGroup]="form">
    <div class="row g-2 mb-2">
      <div class="col-12 col-xl-6">
        <bn-web-ui-card
          class="position-relative"
          data-e2e="ShieldWafEnableWaf"
          heading="WAF"
          inputType="switch"
          [inputValue]="shield()?.wafEnabled || false"
          [isFullHeight]="true"
          [isLoading]="isLoadingToggleWaf()"
          [learnMore]="{ href: 'https://bunny.net/docs/shield/waf' }"
          (valueChanged)="onCardInputChange($event, 'wafEnabled')">
          Our Web Application Firewall Engine is designed to detect, block, and stop malicious requests before they ever reach your origin. Protect your website
          from exploits, SQL Injections, attacks, and other requests intended to harm your server.
        </bn-web-ui-card>
      </div>
      <div class="col-12 col-xl-6">
        <bn-web-ui-card
          heading="Rule execution mode"
          [isFullHeight]="true">
          <div class="mb-4">
            <p>Ensure you review your event logs for false positives before toggling Blocking mode.</p>
          </div>
          <div class="row">
            <bn-web-ui-radio-tabs
              formControlName="wafExecutionMode"
              [isLoading]="isEngineModeUpdating()"
              [items]="engineModes | mapWafEnumToFormListItems" />
          </div>
        </bn-web-ui-card>
      </div>

      <div class="col-12 col-xl-6">
        <bn-web-ui-card
          heading="Request body limit action"
          [isFullHeight]="true">
          <div class="mb-4">
            <p>Control whether to block, log or ignore when the body length exceeds your plan limit.</p>
          </div>
          <div class="row">
            <bn-web-ui-radio-tabs
              formControlName="requestBodyLimitAction"
              [isLoading]="isRequestBodyLimitActionUpdating()"
              [items]="requestBodyLimitActionItems" />
          </div>
        </bn-web-ui-card>
      </div>
      <div class="col-12 col-xl-6">
        <bn-web-ui-card
          heading="Response body limit action"
          [isFullHeight]="true">
          <div class="mb-4">
            <p>Control whether to block, log or ignore when the body length exceeds your plan limit.</p>
          </div>
          <div class="row">
            <bn-web-ui-radio-tabs
              formControlName="responseBodyLimitAction"
              [isLoading]="isResponseBodyLimitActionUpdating()"
              [items]="responseBodyLimitActionItems" />
          </div>
        </bn-web-ui-card>
      </div>
    </div>
    <div class="row g-2 mb-2">
      <div class="col-12">
        <bn-web-ui-card
          data-e2e="ShieldWafEnableRealtimeThreatIntelligence"
          heading="Real-time threat intelligence"
          inputType="switch"
          [disableTitle]="shield()?.planType === PlanType.Basic"
          [headingBadges]="[
            {
              context: 'premium',
              text: 'Advanced',
              size: 'sm',
            },
          ]"
          [inputValue]="shield()?.wafRealtimeThreatIntelligenceEnabled || false"
          [isFullHeight]="true"
          [isInputDisabled]="shield()?.planType === PlanType.Basic"
          [isLoading]="isLoadingToggleRealtimeThreatIntelligence()"
          [tooltip]="shield()?.planType === PlanType.Basic ? upgradeToAdvancedTooltipTmpl : ''"
          (valueChanged)="onCardInputChange($event, 'wafRealtimeThreatIntelligenceEnabled')">
          <p>Real-time threat intelligence delivers zero-day protection by instantly detecting and blocking emerging threats on your WAF.</p>
        </bn-web-ui-card>
      </div>
    </div>
    <div class="row my-2">
      <div class="col-xl-12">
        <bn-web-ui-card>
          <div class="flex-column d-flex gap-2">
            <div>
              <h3 class="mt-2 mb-xl-0">Allowed HTTP versions</h3>
              <p class="mt-1">Restrict and manage unused HTTP versions.</p>
              <bn-web-ui-select
                formControlName="allowed_http_versions"
                size="md"
                [alter]="false"
                [id]="'waf-httpVersions'"
                [isLoading]="this.isLoading.allowed_http_versions()"
                [isMinHeight]="true"
                [items]="allowed_http_versions | mapArrayOfStringsToSelectItems: form.controls.allowed_http_versions.value"
                [multiple]="true"
                [options]="options" />
            </div>

            <div>
              <h3 class="mt-2 mb-xl-0">Allowed HTTP methods</h3>
              <p class="mt-1">Restrict and manage unused HTTP methods.</p>
              <bn-web-ui-select
                formControlName="allowed_methods"
                size="md"
                [alter]="true"
                [id]="'waf-httpMethods'"
                [isLoading]="this.isLoading.allowed_methods()"
                [isMinHeight]="true"
                [items]="allowed_methods | mapArrayOfStringsToSelectItems: form.controls.allowed_methods.value"
                [multiple]="true"
                [options]="options" />
            </div>

            <div>
              <h3 class="mt-2 mb-xl-0">Allowed request content types</h3>
              <p class="mt-1">Restrict and manage unused content types.</p>
              <bn-web-ui-select
                formControlName="allowed_request_content_type"
                size="md"
                [alter]="false"
                [id]="'waf-requestContentTypes'"
                [isLoading]="this.isLoading.allowed_request_content_type()"
                [isMinHeight]="true"
                [items]="allowed_request_content_type | mapArrayOfStringsToSelectItems: form.controls.allowed_request_content_type.value"
                [multiple]="true"
                [options]="options" />
            </div>
          </div>
        </bn-web-ui-card>
      </div>
    </div>
  </form>

  <div class="flex-column d-flex gap-2">
    <bn-web-ui-card
      heading="Rule sensitivity"
      [isLoading]="isLoadingRuleSensitivity()">
      <p>WAF rule sensitivity adjusts protection levels, ranging from Low for minimal restrictions to Extreme for maximum security.</p>

      <app-app-shield-sensitivity-picker
        [activeItem]="activeTemplate"
        [items]="levelTemplates"
        (itemClick)="onTemplatePickerClick($event)" />

      <div class="flex-column d-flex gap-6 pt-3">
        <div class="row d-flex justify-content-between">
          <div class="col-xl-6">
            <h3>Detection paranoia level</h3>
            <p class="mt-1 mb-2 mb-xl-0">Determines the severity level of rules that will trigger a detection log.</p>
          </div>
          <div class="col-xl-5 align-content-center user-select-none">
            <bn-web-ui-level
              customLoadingHeight="42px"
              [isLoading]="isLoading.detection_paranoia_level()"
              [levels]="levelLabels"
              [ngModel]="levelIndexes.detection_paranoia_level"
              (ngModelChange)="onSensibilityChange('detection_paranoia_level', $event)" />
          </div>
        </div>

        <div class="row d-flex justify-content-between">
          <div class="col-xl-6">
            <h3>Blocking paranoia level</h3>
            <p class="mt-1 mb-2 mb-xl-0">Determines the severity level of rules that will block requests.</p>
          </div>
          <div class="col-xl-5 align-content-center user-select-none">
            <bn-web-ui-level
              customLoadingHeight="42px"
              [isLoading]="isLoading.blocking_paranoia_level()"
              [levels]="levelLabels"
              [ngModel]="levelIndexes.blocking_paranoia_level"
              (ngModelChange)="onSensibilityChange('blocking_paranoia_level', $event)" />
          </div>
        </div>

        <div class="row d-flex justify-content-between">
          <div class="col-xl-6">
            <h3>Execution paranoia level</h3>
            <p class="mt-1 mb-2 mb-xl-0">Determines the severity level of rules that will trigger rule execution and their associated actions.</p>
          </div>
          <div class="col-xl-5 align-content-center user-select-none">
            <bn-web-ui-level
              customLoadingHeight="42px"
              [isLoading]="isLoading.executing_paranoia_level()"
              [levels]="levelLabels"
              [ngModel]="levelIndexes.executing_paranoia_level"
              (ngModelChange)="onSensibilityChange('executing_paranoia_level', $event)" />
          </div>
        </div>
      </div>
    </bn-web-ui-card>

    @if (false) {
      <bn-web-ui-loading borderRadius="lg">Loading</bn-web-ui-loading>
    }
  </div>
</div>

<ng-template #upgradeToAdvancedTooltipTmpl>Upgrade to paid plan<br />to use this feature.</ng-template>
